/**
 * Early access password modal for pages with the custom.password_protected_d2c metafield.
 * Password validation via SHA-256 hash, unlock state persisted in localStorage so visitors
 * only need to enter the password once per browser.
 */
(function () {
  const STORAGE_KEY = 'rr_early_access_password_unlocked';

  let snapshotHTML = null;
  let watchdogTimer = null;
  let unlocked = false;

  function startWatchdog() {
    if (watchdogTimer || !snapshotHTML) return;
    watchdogTimer = setInterval(() => {
      if (unlocked) {
        clearInterval(watchdogTimer);
        watchdogTimer = null;
        return;
      }
      if (!document.querySelector('early-access-password')) {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = snapshotHTML;
        const restored = wrapper.firstElementChild;
        if (restored) document.body.appendChild(restored);
      }
    }, 1000);
  }

  async function sha256Hex(input) {
    const buffer = new TextEncoder().encode(input);
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  class EarlyAccessPassword extends HTMLElement {
    async connectedCallback() {
      this.expectedHash = (this.dataset.passwordHash || '').toLowerCase();

      if (this.isUnlocked()) {
        unlocked = true;
        this.remove();
        return;
      }

      // Bypass via configurable URL parameter — only when a bypass token is configured
      const bypassHash = (this.dataset.urlParamHash || '').toLowerCase();
      if (bypassHash) {
        const urlParamName = this.dataset.urlParam || 'eap';
        const params = new URLSearchParams(window.location.search);
        const urlToken = params.get(urlParamName);
        if (urlToken) {
          const hash = await sha256Hex(urlToken);
          if (hash === bypassHash) {
            try {
              localStorage.setItem(STORAGE_KEY, this.expectedHash);
            } catch (_e) {}
            params.delete(urlParamName);
            const qs = params.toString();
            history.replaceState(
              null,
              '',
              window.location.pathname + (qs ? '?' + qs : '') + window.location.hash
            );
            unlocked = true;
            this.remove();
            return;
          }
        }
      }

      if (snapshotHTML === null) snapshotHTML = this.outerHTML;

      this.input = this.querySelector('.early-access-password__input');
      this.submit = this.querySelector('.early-access-password__submit');
      this.error = this.querySelector('.early-access-password__error');
      this.form = this.querySelector('.early-access-password__form');

      this.input.addEventListener('input', () => this.handleInput());
      this.form.addEventListener('submit', (event) => this.handleSubmit(event));

      this.show();
      startWatchdog();
    }

    isUnlocked() {
      try {
        return localStorage.getItem(STORAGE_KEY) === this.expectedHash;
      } catch (_e) {
        return false;
      }
    }

    show() {
      this.hidden = false;
      document.documentElement.classList.add('early-access-password-active');
      requestAnimationFrame(() => this.input.focus({ preventScroll: true }));
    }

    hide() {
      unlocked = true;
      document.documentElement.classList.remove('early-access-password-active');
      this.remove();
    }

    handleInput() {
      this.submit.disabled = this.input.value.trim().length === 0;
      this.error.hidden = true;
      this.input.classList.remove('early-access-password__input--error');
    }

    async handleSubmit(event) {
      event.preventDefault();
      const value = this.input.value.trim();
      if (!value) return;

      // Case-insensitive: the expected hash is built from the downcased password in Liquid
      const hash = await sha256Hex(value.toLowerCase());
      if (hash === this.expectedHash) {
        try {
          localStorage.setItem(STORAGE_KEY, this.expectedHash);
        } catch (_e) {}
        this.hide();
      } else {
        this.error.hidden = false;
        this.input.classList.add('early-access-password__input--error');
        this.input.select();
      }
    }
  }

  if (!customElements.get('early-access-password')) {
    customElements.define('early-access-password', EarlyAccessPassword);
  }
})();
